import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const glassStyle = {
  background: `linear-gradient(
    135deg,
    rgba(255,255,255,0.18) 0%,
    rgba(255,255,255,0.06) 40%,
    rgba(180,255,200,0.08) 70%,
    rgba(255,255,255,0.04) 100%
  )`,
  backdropFilter: "blur(5px) saturate(120%)",
  WebkitBackdropFilter: "blur(5px) saturate(120%)",
  border: "1px solid rgba(255,255,255,0.22)",
};

const GlassCard: FC<{
  title: string;
  description: string;
  buttonLabel: string;
  onExplore: () => void;
}> = ({ title, description, buttonLabel, onExplore }) => (
  <div
    className="absolute bottom-6 left-4 sm:bottom-8 sm:left-6 md:left-20
               w-[85%] sm:w-64 md:w-72 lg:w-80
               rounded-2xl p-3 sm:p-4 overflow-hidden"
    style={glassStyle}
  >
    {/* Top shine streak */}
    <div
      className="absolute top-0 left-[10%] w-[60%] h-px"
      style={{
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
      }}
    />
    {/* Upper gloss */}
    <div
      className="absolute top-0 left-0 right-0 rounded-t-2xl pointer-events-none"
      style={{
        height: "45%",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 100%)",
      }}
    />

    <h2
      className="text-base sm:text-xl md:text-2xl font-semibold leading-snug mb-1.5 sm:mb-2 tracking-wide"
      style={{ color: "rgba(255,255,255,0.95)" }}
    >
      {title}
    </h2>

    <p
      className="text-[0.70rem] sm:text-[0.75rem] leading-relaxed mb-3 sm:mb-4"
      style={{ color: "white" }}
    >
      {description}
    </p>

    <div
      className="h-px mb-2 sm:mb-3"
      style={{
        background: "linear-gradient(90deg, white, transparent)",
      }}
    />

    <button
      onClick={onExplore}
      className="text-[0.60rem] sm:text-[0.65rem] tracking-[0.18em] uppercase rounded-lg px-3 sm:px-4 py-1.5 transition-all duration-200 cursor-pointer"
      style={{
        color: "white",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid white",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.02)")
      }
    >
      {buttonLabel}
    </button>
  </div>
);

const Portfoliotwo: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen grid grid-cols-1 md:grid-cols-2 gap-0">

      <div className="relative w-full h-[50vh] md:h-full overflow-hidden">
        <img
          src="/polythene.webp"
          alt="Polyethene Greenhouse"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <GlassCard
          title={t('portfolio_card1_title')}
          description={t('portfolio_card1_desc')}
          buttonLabel={t('portfolio_explore_label')}
          onExplore={() => navigate('/polythene')}
        />
      </div>

      <div className="relative w-full h-[50vh] md:h-full overflow-hidden">
        <img
          src="/screenhouse-main.webp"
          alt="Screen House"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <GlassCard
          title={t('portfolio_card2_title')}
          description={t('portfolio_card2_desc')}
          buttonLabel={t('portfolio_explore_label')}
          onExplore={() => navigate('/screen-house')}
        />
      </div>

      <div className="relative w-full h-[50vh] md:h-full overflow-hidden">
        <img
          src="/h3.webp"
          alt="Hydroponics"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <GlassCard
          title={t('portfolio_card3_title')}
          description={t('portfolio_card3_desc')}
          buttonLabel={t('portfolio_explore_label')}
          onExplore={() => navigate('/hydroponics')}
        />
      </div>

      <div className="relative w-full h-[50vh] md:h-full overflow-hidden">
        <img
          src="/polycarbonate-main.webp"
          alt="Polycarbonate Greenhouse"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <GlassCard
          title={t('portfolio_card4_title')}
          description={t('portfolio_card4_desc')}
          buttonLabel={t('portfolio_explore_label')}
          onExplore={() => navigate('/polycarbonate')}
        />
      </div>

    </div>
  );
};

export default Portfoliotwo;